import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Search, Plus, Zap, DollarSign, RotateCcw, Settings, AlertTriangle, Download, X as XIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Switch } from '@/components/ui/switch';
import { SearchableSelect } from '@/components/SearchableSelect';
import RichTextEditor from '@/components/RichTextEditor';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { LIABILITIES_REFRESH_EVENT } from '@/components/LiabilitiesByBankWidget';
import { InvoiceDetailPanel } from './invoices/InvoiceDetailPanel';
import { RecurringTemplatesTab } from './invoices/RecurringTemplatesTab';
import { ProjectHoursTab } from './invoices/ProjectHoursTab';

const statusColors = {
  'Draft': 'bg-zinc-500/15 text-zinc-400',
  'Review': 'bg-amber-500/15 text-amber-400',
  'Approved': 'bg-blue-500/15 text-blue-400',
  'Sent': 'bg-sky-500/15 text-sky-400',
  'Partial': 'bg-amber-500/15 text-amber-400',
  'Paid': 'bg-emerald-500/15 text-emerald-400',
  'Cancelled': 'bg-red-500/15 text-red-400',
};

export default function InvoicesPage() {
  const { effectiveRole } = useAuth();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('invoices');
  const [invoices, setInvoices] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [projectFilter, setProjectFilter] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [urgencyFilter, setUrgencyFilter] = useState(''); // '' | 'overdue' | 'due_week'
  const [typeFilter, setTypeFilter] = useState(''); // '' | 'project' | 'dd' - Phase 67 (DD recovery)
  const [projects, setProjects] = useState([]);

  // Create invoice
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ project_id: '', billable_month: '', notes: '', po_reference: '' });

  // Auto-draft — scans for un-invoiced approved work since the last invoice.
  // No month picker: each project's window is computed server-side as
  // [day after last Sent invoice's reference_period_end, today] (or
  // [earliest approved timesheet, today] for never-invoiced projects).
  const [autoDraftOpen, setAutoDraftOpen] = useState(false);
  const [autoDraftPreviews, setAutoDraftPreviews] = useState([]);
  const [autoDraftSelected, setAutoDraftSelected] = useState({});
  const [autoDraftLoading, setAutoDraftLoading] = useState(false);
  const [autoDraftScanned, setAutoDraftScanned] = useState(false);
  const [autoDraftIncludeReimb, setAutoDraftIncludeReimb] = useState(true);

  // Detail
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);

  // Recurring tab visibility
  const [recurringVisible, setRecurringVisible] = useState(false);

  // Bulk PDF export selection
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState({}); // { invoice_id: true }
  const [bulkExporting, setBulkExporting] = useState(false);

  const selectedCount = Object.values(selectedInvoiceIds).filter(Boolean).length;
  const toggleInvoiceSelected = (id, e) => {
    e?.stopPropagation?.();
    setSelectedInvoiceIds(prev => {
      const next = { ...prev };
      if (next[id]) delete next[id];
      else next[id] = true;
      return next;
    });
  };
  const clearSelection = () => setSelectedInvoiceIds({});

  const bulkExportPdf = async () => {
    const ids = Object.entries(selectedInvoiceIds).filter(([, v]) => v).map(([k]) => k);
    if (ids.length === 0) return toast.error('Select at least one invoice');
    setBulkExporting(true);
    try {
      const r = await api.post('/invoices/bulk-export-pdf', { invoice_ids: ids }, { responseType: 'blob' });
      const rendered = r.headers?.['x-bulk-rendered'] ?? ids.length;
      const skipped = parseInt(r.headers?.['x-bulk-skipped'] ?? '0', 10) || 0;
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      const a = document.createElement('a'); a.style.display = 'none'; a.href = url; a.download = `Invoices_Bulk_Export_${ts}.pdf`;
      document.body.appendChild(a); a.click();
      setTimeout(() => { document.body.removeChild(a); window.URL.revokeObjectURL(url); }, 250);
      if (skipped > 0) {
        toast.success(`Exported ${rendered} invoice${rendered === 1 ? '' : 's'} (skipped ${skipped} that failed to render)`);
      } else {
        toast.success(`Exported ${rendered} invoice${rendered === 1 ? '' : 's'} into one PDF`);
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Bulk export failed'); }
    setBulkExporting(false);
  };

  const fetchInvoices = () => {
    return api.get('/invoices')
      .then(r => {
        setInvoices(r.data);
        // Notify Liabilities widget + Accounting Overview cards (Total Invoiced,
        // Collected, Expenses, Net Income) so they stay in sync without a reload.
        window.dispatchEvent(new Event(LIABILITIES_REFRESH_EVENT));
      });
  };
  useEffect(() => {
    fetchInvoices();
    api.get('/projects').then(r => setProjects(r.data)).catch(() => {});
    api.get('/settings/recurring-templates-visible').then(r => setRecurringVisible(r.data.recurring_templates_visible)).catch(() => {});
  }, []);

  
  // Sync status filter when URL ?status= changes (e.g., deep-link from Dashboard widgets)
  useEffect(() => {
    const urlStatus = searchParams.get('status') || '';
    setStatusFilter(urlStatus);
  }, [searchParams]);

  // Auto-open invoice detail when arriving via ?id= deeplink (e.g. from the
  // Proposal Due Diligence tab "View Invoice" button).
  useEffect(() => {
    const urlId = searchParams.get('id');
    if (urlId && invoices.length > 0) {
      const inv = invoices.find(i => i.id === urlId);
      if (inv) {
        setSelectedInvoiceId(inv.id);
        setDetailOpen(true);
      }
    }
  }, [searchParams, invoices.length]);
  

  const prefillCreateInvoice = (projectId, month) => {
    const currentMonth = new Date().toISOString().slice(0, 7);
    setCreateForm({ project_id: projectId, billable_month: month || currentMonth, notes: '', po_reference: '', net_terms: 30 });
    setActiveTab('invoices');
    setCreateOpen(true);
  };

  const openDetail = (inv) => {
    setSelectedInvoiceId(inv.id);
    setDetailOpen(true);
  };

  const handleCreate = async () => {
    if (!createForm.project_id) return toast.error('Project is required');
    if (!createForm.billable_month) return toast.error('Billable month is required');
    try {
      const r = await api.post('/invoices', createForm);
      toast.success(`Invoice ${r.data.invoice_number} created`);
      setCreateOpen(false);
      fetchInvoices();
      setSelectedInvoiceId(r.data.id);
      setDetailOpen(true);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error creating invoice'); }
  };

  const autoDraftPreview = async () => {
    setAutoDraftLoading(true);
    try {
      const r = await api.post('/invoices/auto-draft/preview', {});
      setAutoDraftPreviews(r.data.previews || []);
      const sel = {};
      (r.data.previews || []).forEach(p => { sel[p.project_id] = true; });
      setAutoDraftSelected(sel);
      setAutoDraftScanned(true);
      if ((r.data.previews || []).length === 0) toast.info('No un-invoiced work found. Every project with approved hours either already has a Draft invoice or is fully billed.');
    } catch (e) { toast.error(e.response?.data?.detail || 'Preview failed'); }
    setAutoDraftLoading(false);
  };

  const autoDraftGenerate = async () => {
    const projectIds = Object.entries(autoDraftSelected).filter(([, v]) => v).map(([k]) => k);
    if (projectIds.length === 0) return toast.error('Select at least one project');
    setAutoDraftLoading(true);
    try {
      const r = await api.post('/invoices/auto-draft/generate', { project_ids: projectIds, include_reimbursements: autoDraftIncludeReimb });
      toast.success(`${r.data.count || 0} draft invoice(s) created`);
      setAutoDraftOpen(false); setAutoDraftPreviews([]); fetchInvoices();
    } catch (e) { toast.error(e.response?.data?.detail || 'Generation failed'); }
    setAutoDraftLoading(false);
  };

  const statusOrder = { 'Draft': 0, 'Review': 1, 'Approved': 2, 'Sent': 3, 'Partial': 4, 'Paid': 5, 'Cancelled': 6 };

  // Urgency classification for Sent/Partial invoices.
  // Upon Receipt gets a 3-day grace (day 0–3 = due-soon amber, day 4+ = overdue red).
  // Pay when Paid never shows urgency (waits for client payment).
  // Net-N invoices use the stored due_date directly.
  const computeUrgency = (inv) => {
    if (!['Sent', 'Partial'].includes(inv.status)) return null;
    if (!inv.due_date) return null;
    const nt = Number(inv.net_terms);
    // Pay when Paid: render a neutral pill, no urgency math
    if (nt === -1) {
      return { tier: 'pay_when_paid', label: 'Pay when Paid', tone: 'bg-violet-500/15 text-violet-400 border-violet-500/30' };
    }
    const now = new Date(); now.setHours(0, 0, 0, 0);
    const due = new Date(inv.due_date + 'T00:00:00').getTime();
    const days = Math.round((due - now.getTime()) / 86400000);
    const isUponReceipt = nt === 0;
    if (isUponReceipt) {
      // Grace: day 0-3 = due-soon, day 4+ = overdue
      if (days <= -4) return { tier: 'overdue', label: `Overdue ${Math.abs(days)}d`, tone: 'bg-destructive/15 text-destructive border-destructive/30' };
      if (days <= 0) return { tier: 'due_soon', label: days === 0 ? 'Due today' : `Due ${Math.abs(days)}d ago (grace)`, tone: 'bg-amber-500/15 text-amber-400 border-amber-500/30' };
      return { tier: 'due_soon', label: 'Upon Receipt', tone: 'bg-amber-500/15 text-amber-400 border-amber-500/30' };
    }
    if (days < 0) return { tier: 'overdue', label: `Overdue ${Math.abs(days)}d`, tone: 'bg-destructive/15 text-destructive border-destructive/30' };
    if (days === 0) return { tier: 'due_soon', label: 'Due today', tone: 'bg-destructive/15 text-destructive border-destructive/30' };
    if (days <= 7) return { tier: 'due_soon', label: `Due in ${days}d`, tone: 'bg-amber-500/15 text-amber-400 border-amber-500/30' };
    if (days <= 30) return { tier: 'due_later', label: `Due in ${days}d`, tone: 'bg-blue-500/15 text-blue-400 border-blue-500/30' };
    return { tier: 'future', label: formatShortDate(inv.due_date), tone: 'border-border text-muted-foreground' };
  };

  // Urgency rank for sorting (lower = more urgent)
  const urgencyRank = (inv) => {
    const u = computeUrgency(inv);
    if (!u) return 99;
    if (u.tier === 'overdue') return 0;
    if (u.tier === 'due_soon') return 1;
    if (u.tier === 'due_later') return 2;
    if (u.tier === 'pay_when_paid') return 4; // park below dated invoices
    return 3;
  };

  const filtered = invoices.filter(i => {
    const matchesSearch = (i.invoice_number || '').toLowerCase().includes(search.toLowerCase()) || (i.project_name || '').toLowerCase().includes(search.toLowerCase()) || (i.client_name || '').toLowerCase().includes(search.toLowerCase());
    const matchesStatus = !statusFilter || i.status === statusFilter;
    const matchesProject = !projectFilter || i.project_id === projectFilter;
    const matchesClient = !clientFilter || i.client_id === clientFilter;
    // Urgency filter chip
    if (urgencyFilter === 'overdue' && urgencyRank(i) !== 0) return false;
    if (urgencyFilter === 'due_week' && urgencyRank(i) > 1) return false;
    // Type filter chip - DD invoices have proposal_id and no project_id; project invoices have project_id.
    if (typeFilter === 'dd' && !i.proposal_id) return false;
    if (typeFilter === 'project' && !i.project_id) return false;
    return matchesSearch && matchesStatus && matchesProject && matchesClient;
  }).sort((a, b) => {
    // Primary: status order (Draft → Cancelled). Within Sent/Partial rows, urgency wins.
    const sa = statusOrder[a.status] ?? 99;
    const sb = statusOrder[b.status] ?? 99;
    if (sa !== sb) return sa - sb;
    return urgencyRank(a) - urgencyRank(b);
  });

  // KPI: overdue across the full invoice list (not filtered) so the "am I ok today?" answer is consistent
  const overdueInvoices = invoices.filter(i => urgencyRank(i) === 0);
  const overdueTotal = overdueInvoices.reduce((s, i) => s + (i.balance_due || 0), 0);
  const oldestOverdueDays = overdueInvoices.length
    ? Math.max(...overdueInvoices.map(i => {
        const now = new Date(); now.setHours(0, 0, 0, 0);
        const due = new Date(i.due_date + 'T00:00:00').getTime();
        return Math.round((now.getTime() - due) / 86400000);
      }))
    : 0;

  // Totals reflect the currently filtered result set (not the full invoice list)
  const totalBilled = filtered.reduce((s, i) => s + (i.total || 0), 0);
  const totalOutstanding = filtered.reduce((s, i) => { if (['Draft', 'Review', 'Approved', 'Paid', 'Cancelled'].includes(i.status)) return s; return s + (i.balance_due || 0); }, 0);
  const currentMonth = new Date().toISOString().slice(0, 7);

  const formatShortDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const d = new Date(dateStr.includes('T') ? dateStr : `${dateStr}T00:00:00`);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch { return dateStr; }
  };

  const dueDisplay = (inv) => {
    const nt = inv.net_terms;
    if (nt === -1 || nt === '-1' || nt === 'Pay when Paid') return 'Pay when Paid';
    if (nt === 0 || nt === '0' || nt === 'Upon Receipt') return 'Upon Receipt';
    if (inv.due_date) return formatShortDate(inv.due_date);
    return null;
  };

  const isAdmin = effectiveRole === 'Administrator';
  const showRecurringTab = recurringVisible || isAdmin;

  const toggleRecurringVisible = async () => {
    const newVal = !recurringVisible;
    try {
      await api.put('/settings/recurring-templates-visible', { value: newVal });
      setRecurringVisible(newVal);
      toast.success(newVal ? 'Recurring Templates tab is now visible to all users' : 'Recurring Templates tab hidden from non-admin users');
      if (!newVal && activeTab === 'recurring') setActiveTab('invoices');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error updating setting'); }
  };

  return (
    <div className="space-y-4 animate-fade-in" data-testid="invoices-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="flex items-baseline gap-3 text-[22px] font-medium tracking-tight">Invoices<span className="text-sm text-muted-foreground/70 font-normal tabular-nums">{invoices.length}</span></h1>
        </div>
        <div className="flex gap-2">
          {activeTab === 'invoices' && (
            <>
              <Button variant="outline" onClick={() => { setAutoDraftPreviews([]); setAutoDraftSelected({}); setAutoDraftScanned(false); setAutoDraftIncludeReimb(true); setAutoDraftOpen(true); autoDraftPreview(); }} data-testid="auto-draft-btn"><Zap className="h-4 w-4 mr-1" /> Auto-Draft</Button>
              <Button onClick={() => { setCreateForm({ project_id: '', billable_month: currentMonth, notes: '', po_reference: '', net_terms: 30 }); setCreateOpen(true); }} className="bg-primary text-primary-foreground" data-testid="create-invoice-btn"><Plus className="h-4 w-4 mr-1" /> New Invoice</Button>
            </>
          )}
        </div>
      </div>

      {/* Tab Bar */}
      <div className="flex items-center gap-1 border-b border-border" data-testid="invoices-tab-bar">
        <button
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'invoices' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          onClick={() => setActiveTab('invoices')}
          data-testid="tab-invoices"
        >
          Invoices
        </button>
        <button
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${activeTab === 'project-hours' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          onClick={() => setActiveTab('project-hours')}
          data-testid="tab-project-hours"
        >
          Project Hours & Billing
        </button>
        {showRecurringTab && (
          <button
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${activeTab === 'recurring' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
            onClick={() => setActiveTab('recurring')}
            data-testid="tab-recurring"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Recurring Templates
          </button>
        )}
        {isAdmin && (
          <div className="ml-auto flex items-center gap-2 pb-1" data-testid="recurring-visibility-toggle">
            <Settings className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">Show Recurring tab</span>
            <Switch checked={recurringVisible} onCheckedChange={toggleRecurringVisible} data-testid="recurring-visibility-switch" />
          </div>
        )}
      </div>

      {activeTab === 'recurring' ? (
        <RecurringTemplatesTab />
      ) : activeTab === 'project-hours' ? (
        <ProjectHoursTab onCreateInvoice={prefillCreateInvoice} />
      ) : (
      <>
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative max-w-xs flex-1">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search invoices..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 bg-card border-border" data-testid="invoices-search" />
        </div>
        <SearchableSelect
          value={projectFilter || 'all'}
          onValueChange={v => setProjectFilter(v === 'all' ? '' : v)}
          options={[
            { value: 'all', label: 'All Projects' },
            ...[...new Map(invoices.filter(i => i.project_id).map(i => [i.project_id, i.project_name])).entries()].map(([id, name]) => ({ value: id, label: name }))
          ]}
          placeholder="All Projects"
          searchPlaceholder="Search projects..."
          className="w-52"
          data-testid="invoices-project-filter"
        />
        <SearchableSelect
          value={clientFilter || 'all'}
          onValueChange={v => setClientFilter(v === 'all' ? '' : v)}
          options={[
            { value: 'all', label: 'All Clients' },
            ...[...new Map(invoices.filter(i => i.client_id).map(i => [i.client_id, i.client_name])).entries()].map(([id, name]) => ({ value: id, label: name }))
          ]}
          placeholder="All Clients"
          searchPlaceholder="Search clients..."
          className="w-48"
          data-testid="invoices-client-filter"
        />
      </div>

      {/* Status Filter Chips */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {['', 'Draft', 'Review', 'Approved', 'Sent', 'Partial', 'Paid', 'Cancelled'].map(s => {
          const count = s ? invoices.filter(i => i.status === s).length : invoices.length;
          return (
            <button key={s} className={`px-3 py-1 text-xs rounded-full border transition-colors ${statusFilter === s ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:text-foreground hover:border-foreground/20'}`} onClick={() => setStatusFilter(s)} data-testid={`status-chip-${s || 'all'}`}>{s || 'All'} ({count})</button>
          );
        })}
      </div>

      {/* Summary Bar */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="invoice-summary-cards">
        <Card className="border-border bg-card shadow-none"><CardContent className="p-3 flex items-center justify-between"><div className="flex items-center gap-2"><DollarSign className="h-4 w-4 text-primary" /><p className="text-xs text-muted-foreground">Total Billed</p></div><p className="text-lg font-heading font-bold text-primary">${totalBilled.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p></CardContent></Card>
        <Card className="border-border bg-card shadow-none"><CardContent className="p-3 flex items-center justify-between"><div className="flex items-center gap-2"><DollarSign className="h-4 w-4 text-amber-400" /><p className="text-xs text-muted-foreground">Outstanding</p></div><p className="text-lg font-heading font-bold text-amber-400">${totalOutstanding.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p></CardContent></Card>
        <Card
          className={`shadow-none cursor-pointer transition-colors ${overdueInvoices.length ? 'border-destructive/30 bg-destructive/5 hover:bg-destructive/10' : 'border-border bg-card'}`}
          onClick={() => { if (overdueInvoices.length) { setUrgencyFilter('overdue'); setStatusFilter(''); } }}
          data-testid="invoice-overdue-kpi"
        >
          <CardContent className="p-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle className={`h-4 w-4 ${overdueInvoices.length ? 'text-destructive' : 'text-muted-foreground'}`} />
              <div>
                <p className="text-xs text-muted-foreground">Overdue</p>
                {overdueInvoices.length > 0 && <p className="text-xs text-muted-foreground">{overdueInvoices.length} invoice{overdueInvoices.length === 1 ? '' : 's'} · oldest {oldestOverdueDays}d</p>}
              </div>
            </div>
            <p className={`text-lg font-heading font-bold ${overdueInvoices.length ? 'text-destructive' : 'text-muted-foreground'}`}>
              {overdueInvoices.length ? `$${overdueTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '-'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Type Filter Chips - Project Invoices vs Due Diligence quotes (Phase 67) */}
      <div className="flex items-center gap-1.5 flex-wrap" data-testid="invoice-type-chips">
        <span className="text-xs uppercase tracking-wider text-muted-foreground mr-1">Type:</span>
        {(() => {
          const projectCount = invoices.filter(i => i.project_id).length;
          const ddCount = invoices.filter(i => i.proposal_id).length;
          return [
            { key: '', label: `All (${invoices.length})` },
            { key: 'project', label: `Project Invoices (${projectCount})`, tone: 'text-sky-300 border-sky-500/30' },
            { key: 'dd', label: `Due Diligence (${ddCount})`, tone: 'text-violet-300 border-violet-500/30' },
          ].map(c => {
            const active = typeFilter === c.key;
            return (
              <button
                key={c.key || 'all'}
                type="button"
                onClick={() => setTypeFilter(c.key)}
                className={`px-3 py-1 text-xs rounded-full border transition-colors ${active ? 'bg-primary text-primary-foreground border-primary' : `border-border text-muted-foreground hover:text-foreground hover:border-foreground/20 ${c.tone || ''}`}`}
                data-testid={`type-chip-${c.key || 'all'}`}
              >
                {c.label}
              </button>
            );
          });
        })()}
      </div>

      {/* Urgency Filter Chips */}
      <div className="flex items-center gap-1.5 flex-wrap" data-testid="invoice-urgency-chips">
        {[
          { key: '', label: 'All Urgency' },
          { key: 'overdue', label: `Overdue${overdueInvoices.length ? ` (${overdueInvoices.length})` : ''}`, tone: 'text-destructive border-destructive/30' },
          { key: 'due_week', label: 'Due This Week', tone: 'text-amber-400 border-amber-500/30' },
        ].map(c => {
          const active = urgencyFilter === c.key;
          return (
            <button
              key={c.key || 'all'}
              type="button"
              onClick={() => setUrgencyFilter(c.key)}
              className={`px-3 py-1 text-xs rounded-full border transition-colors ${active ? 'bg-primary text-primary-foreground border-primary' : `border-border text-muted-foreground hover:text-foreground hover:border-foreground/20 ${c.tone || ''}`}`}
              data-testid={`urgency-chip-${c.key || 'all'}`}
            >
              {c.label}
            </button>
          );
        })}
      </div>

      {/* Bulk Selection Action Bar */}
      {selectedCount > 0 && (
        <div className="flex items-center justify-between gap-3 rounded-md border border-primary/30 bg-primary/10 px-3 py-2" data-testid="bulk-action-bar">
          <p className="text-xs flex items-center gap-2">
            <span><span className="font-semibold">{selectedCount}</span> invoice{selectedCount === 1 ? '' : 's'} selected</span>
            {(() => {
              const visibleSelected = filtered.filter(i => selectedInvoiceIds[i.id]).length;
              const hidden = selectedCount - visibleSelected;
              return hidden > 0 ? (
                <Badge variant="outline" className="text-xs border-amber-500/40 text-amber-300 bg-amber-500/5" data-testid="bulk-hidden-pill">
                  {hidden} hidden by current filter
                </Badge>
              ) : null;
            })()}
          </p>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={clearSelection} data-testid="bulk-clear-btn">
              <XIcon className="h-3 w-3 mr-1" /> Clear
            </Button>
            <Button size="sm" onClick={bulkExportPdf} disabled={bulkExporting} className="bg-primary text-primary-foreground h-7 text-xs" data-testid="bulk-export-pdf-btn">
              <Download className="h-3 w-3 mr-1" /> {bulkExporting ? 'Building PDF…' : `Export ${selectedCount} as One PDF`}
            </Button>
          </div>
        </div>
      )}

      {/* Invoice Table */}
      <Card className="border-border bg-card shadow-none">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="w-[40px] text-xs uppercase tracking-wider">
                  <Checkbox
                    checked={filtered.length > 0 && filtered.every(i => selectedInvoiceIds[i.id])}
                    onCheckedChange={(v) => {
                      if (v) {
                        const next = { ...selectedInvoiceIds };
                        filtered.forEach(i => { next[i.id] = true; });
                        setSelectedInvoiceIds(next);
                      } else {
                        const next = { ...selectedInvoiceIds };
                        filtered.forEach(i => { delete next[i.id]; });
                        setSelectedInvoiceIds(next);
                      }
                    }}
                    aria-label="Select all visible invoices"
                    data-testid="bulk-select-all"
                  />
                </TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Invoice #</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Project</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Client</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Month</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-right">Amount</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Status</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Status Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground text-xs py-8">No invoices found.</TableCell></TableRow>
              ) : filtered.map(inv => {
                const due = dueDisplay(inv);
                const urgency = computeUrgency(inv);
                const checked = !!selectedInvoiceIds[inv.id];
                return (
                <TableRow key={inv.id} className={`border-border cursor-pointer hover:bg-secondary/30 transition-colors ${checked ? 'bg-primary/5' : ''}`} onClick={() => openDetail(inv)} data-testid={`invoice-row-${inv.id}`}>
                  <TableCell className="w-[40px]" onClick={(e) => toggleInvoiceSelected(inv.id, e)}>
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => toggleInvoiceSelected(inv.id)}
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`Select ${inv.invoice_number}`}
                      data-testid={`bulk-select-${inv.id}`}
                    />
                  </TableCell>
                  <TableCell className="font-mono text-xs text-primary">{inv.invoice_number}</TableCell>
                  <TableCell className="text-sm">{inv.project_name}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{inv.client_name}</TableCell>
                  <TableCell className="text-xs">{inv.billable_month}</TableCell>
                  <TableCell className="text-sm text-right font-heading font-bold">
                    {(inv.discount_total || 0) > 0 ? (
                      <div className="flex flex-col items-end leading-tight">
                        <span>${(((inv.total || 0) - (inv.discount_total || 0))).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                        <span className="text-xs text-muted-foreground line-through font-normal">${(inv.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                      </div>
                    ) : (
                      <>${(inv.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <Badge status={inv.status} className={`text-xs ${statusColors[inv.status] || ''}`}>{inv.status}</Badge>
                      {urgency ? (
                        <Badge variant="outline" className={`text-xs ${urgency.tone}`} data-testid={`invoice-due-tag-${inv.id}`} title={due ? `Due: ${due}` : undefined}>
                          {urgency.tier === 'overdue' && <AlertTriangle className="h-2.5 w-2.5 mr-1 inline" />}
                          {urgency.label}
                        </Badge>
                      ) : (['Sent', 'Partial'].includes(inv.status) && due && (
                        <Badge variant="outline" className="text-xs border-border text-muted-foreground" data-testid={`invoice-due-tag-${inv.id}`}>Due: {due}</Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap" data-testid={`invoice-status-date-${inv.id}`}>{formatShortDate(inv.status_changed_at)}</TableCell>
                </TableRow>
              );})}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Invoice Sheet */}
      <Sheet open={createOpen} onOpenChange={setCreateOpen}>
        <SheetContent className="sm:max-w-lg overflow-y-auto" data-testid="create-invoice-sheet">
          <SheetHeader><SheetTitle className="font-heading">New Invoice</SheetTitle><SheetDescription>Create a new invoice for a project.</SheetDescription></SheetHeader>
          <div className="mt-4 space-y-3">
            <div>
              <Label className="text-xs text-muted-foreground">Project *</Label>
              <SearchableSelect value={createForm.project_id || ''} onValueChange={v => setCreateForm(f => ({ ...f, project_id: v }))} options={projects.map(p => ({ value: p.id, label: `${p.project_number} - ${p.name}` }))} placeholder="Select project" searchPlaceholder="Search projects..." data-testid="invoice-project" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Billable Month *</Label>
              <Input type="month" value={createForm.billable_month} onChange={e => setCreateForm(f => ({ ...f, billable_month: e.target.value }))} className="bg-background border-border" data-testid="invoice-month" />
              <p className="text-xs text-muted-foreground mt-1">Hours reference window is auto-computed: from the day after the last sent invoice (for this project) through today. Falls back to the billable month if this is the project&apos;s first invoice.</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">P.O. / Reference Number</Label>
              <Input value={createForm.po_reference} onChange={e => setCreateForm(f => ({ ...f, po_reference: e.target.value }))} className="bg-background border-border" placeholder="Optional P.O. or reference number" data-testid="invoice-po-ref" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Net Terms</Label>
              <Select value={String(createForm.net_terms ?? 30)} onValueChange={v => setCreateForm(f => ({ ...f, net_terms: parseInt(v) }))}>
                <SelectTrigger className="bg-background border-border" data-testid="invoice-net-terms"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="0">Upon Receipt</SelectItem>
                  <SelectItem value="-1">Pay when Paid</SelectItem>
                  <SelectItem value="15">Net 15</SelectItem>
                  <SelectItem value="30">Net 30</SelectItem>
                  <SelectItem value="45">Net 45</SelectItem>
                  <SelectItem value="60">Net 60</SelectItem>
                  <SelectItem value="90">Net 90</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Notes</Label>
              <RichTextEditor value={createForm.notes} onChange={v => setCreateForm(f => ({ ...f, notes: v }))} placeholder="Optional notes" collapsedHeight={80} />
            </div>
            <Button onClick={handleCreate} className="w-full bg-primary text-primary-foreground" data-testid="create-invoice-submit"><Plus className="h-4 w-4 mr-1" /> Create Invoice</Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Invoice Detail Panel */}
      <InvoiceDetailPanel open={detailOpen} onOpenChange={setDetailOpen} invoiceId={selectedInvoiceId} onInvoicesRefresh={fetchInvoices} />

      {/* Auto-Draft Sheet */}
      <Sheet open={autoDraftOpen} onOpenChange={setAutoDraftOpen}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="auto-draft-sheet">
          <SheetHeader><SheetTitle className="font-heading text-xl"><Zap className="h-5 w-5 inline mr-2 text-amber-400" />Auto-Draft Uninvoiced Work</SheetTitle><SheetDescription>Creates a draft invoice for each project covering all approved billable hours since the last invoice was sent. Projects with an existing Draft invoice are skipped.</SheetDescription></SheetHeader>
          <div className="mt-4 space-y-4">
            <div className="flex items-center justify-between gap-3">
              <label className="flex items-center gap-2 text-sm cursor-pointer select-none"><Checkbox checked={autoDraftIncludeReimb} onCheckedChange={v => setAutoDraftIncludeReimb(!!v)} data-testid="auto-draft-include-reimb" /><span className="text-muted-foreground">Include approved reimbursements</span></label>
              <Button variant="outline" size="sm" onClick={autoDraftPreview} disabled={autoDraftLoading} data-testid="auto-draft-rescan-btn">{autoDraftLoading ? 'Scanning...' : 'Rescan'}</Button>
            </div>
            {autoDraftLoading && autoDraftPreviews.length === 0 && (
              <p className="text-xs text-muted-foreground text-center py-6">Scanning projects for un-invoiced work...</p>
            )}
            {!autoDraftLoading && autoDraftScanned && autoDraftPreviews.length === 0 && (
              <div className="border border-border bg-background rounded-md p-6 text-center" data-testid="auto-draft-empty">
                <p className="text-sm text-muted-foreground">No un-invoiced work found.</p>
                <p className="text-xs text-muted-foreground mt-1">Every project with approved hours already has a Draft invoice, is fully billed, or has no approved billable timesheets.</p>
              </div>
            )}
            {autoDraftPreviews.length > 0 && (
              <div className="space-y-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{autoDraftPreviews.length} project{autoDraftPreviews.length !== 1 ? 's' : ''} ready to draft</p>
                {autoDraftPreviews.map(p => { const checked = autoDraftSelected[p.project_id] || false; return (
                  <Card key={p.project_id} className={`border-border shadow-none transition-all ${checked ? 'bg-primary/5 border-primary/30' : 'bg-background'}`}>
                    <CardContent className="p-3 space-y-2">
                      <div className="flex items-center gap-2"><Checkbox checked={checked} onCheckedChange={v => setAutoDraftSelected(prev => ({ ...prev, [p.project_id]: !!v }))} data-testid={`auto-draft-check-${p.project_id}`} /><div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{p.project_number} - {p.project_name}</p>{p.client_name && <p className="text-xs text-muted-foreground">{p.client_name}</p>}{(p.reference_period_start || p.reference_period_end) && <p className="text-xs font-mono text-muted-foreground" data-testid={`auto-draft-window-${p.project_id}`}>Covers: {p.reference_period_start || '…'} → {p.reference_period_end || '…'}</p>}</div><div className="text-right"><p className="text-sm font-heading font-bold text-primary">${p.estimated_subtotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p><p className="text-xs text-muted-foreground">{p.total_hours}h approved</p></div></div>
                      <div className="border border-border rounded-md overflow-hidden">
                        <Table>
                          <TableHeader><TableRow className="border-border hover:bg-transparent"><TableHead className="text-xs uppercase text-muted-foreground">Section</TableHead><TableHead className="text-xs uppercase text-muted-foreground text-right">Hours</TableHead><TableHead className="text-xs uppercase text-muted-foreground text-right">Est. Amount</TableHead><TableHead className="text-xs uppercase text-muted-foreground text-right">Remaining Fee</TableHead></TableRow></TableHeader>
                          <TableBody>{p.sections.map(s => (<TableRow key={s.section_id} className="border-border"><TableCell className="text-xs">{s.section_number ? `${s.section_number} - ` : ''}{s.section_name}</TableCell><TableCell className="text-xs text-right">{s.hours.toFixed(1)}h</TableCell><TableCell className="text-xs text-right text-primary font-medium">${s.estimated_amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell><TableCell className="text-xs text-right text-muted-foreground">${s.remaining.toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell></TableRow>))}</TableBody>
                        </Table>
                      </div>
                      {p.available_reimbursements > 0 && <p className="text-xs text-muted-foreground">+ {p.available_reimbursements} reimbursement{p.available_reimbursements !== 1 ? 's' : ''} (${p.reimbursement_total.toFixed(2)})</p>}
                    </CardContent>
                  </Card>
                ); })}
                <div className="border-t border-border pt-3 flex items-center justify-between">
                  <div><p className="text-sm text-muted-foreground">{Object.values(autoDraftSelected).filter(Boolean).length} of {autoDraftPreviews.length} selected</p><p className="text-xs text-muted-foreground mt-0.5">Estimated total: ${autoDraftPreviews.filter(p => autoDraftSelected[p.project_id]).reduce((s, p) => s + p.estimated_subtotal + (autoDraftIncludeReimb ? p.reimbursement_total : 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p></div>
                  <Button onClick={autoDraftGenerate} disabled={autoDraftLoading} className="bg-emerald-500 text-white hover:bg-emerald-600" data-testid="auto-draft-generate-btn"><Zap className="h-4 w-4 mr-1" /> {autoDraftLoading ? 'Generating...' : 'Generate Drafts'}</Button>
                </div>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
      </>
      )}
    </div>
  );
}
